import { currencyFormatter } from "@/lib/display-format";
import type { Language } from "@/lib/finance";

type ChartPoint = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
  payload?: { transactionCount?: number };
};

type FinanceChartTooltipProps = {
  active?: boolean;
  payload?: readonly ChartPoint[];
  label?: string | number;
  language: Language;
  context?: string;
  metricLabels: Record<string, string>;
  labelFormatter?: (value: string) => string;
};

export function FinanceChartTooltip({ active, payload, label, language, context, metricLabels, labelFormatter }: FinanceChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const format = currencyFormatter(language);
  const period = label === undefined ? "" : (labelFormatter ? labelFormatter(String(label)) : String(label));
  return <div className="min-w-48 rounded border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
    {context && <p className="mb-1 font-semibold">{context}</p>}
    {period && <p className="mb-2 text-muted-foreground">{period}</p>}
    <div className="space-y-1.5">{payload.map((point, index) => {
      const key = String(point.dataKey ?? point.name ?? index);
      const value = Number(point.value);
      return <div key={`${key}-${index}`} className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-2"><span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: point.color || "#64748b" }} />{metricLabels[key] || String(point.name || key)}</span>
        <span className="font-semibold tabular-nums">{Number.isFinite(value) ? format.format(value) : "—"}</span>
      </div>;
    })}</div>
    {payload[0]?.payload?.transactionCount !== undefined && <p className="mt-2 border-t pt-2 text-muted-foreground">{payload[0].payload.transactionCount} {language === "id" ? "transaksi" : "transactions"}</p>}
  </div>;
}


/** Compatibility export for category charts; optional options retain older callers. */
type CategoryChartTooltipProps = Partial<FinanceChartTooltipProps> & {
  title?: string;
  [option: string]: unknown;
};

export function CategoryChartTooltip({ title, language = "id", metricLabels = {}, ...props }: CategoryChartTooltipProps) {
  return <FinanceChartTooltip {...props} language={language} metricLabels={metricLabels} context={props.context ?? title} />;
}
